/**
 * The Trash view's retention notice and its Empty Trash control.
 *
 * ## Why it is a component and not markup inside the surface
 *
 * It rendered as a **third column** between the folder rail and the mail list
 * — a sibling of both inside the horizontal flex row — so it squeezed the list
 * instead of spanning it. The styling had always been written for a bar
 * (`justify-between`, `border-b`, full-bleed padding); only its position in the
 * tree was wrong, which is exactly the kind of mistake that recurs when the
 * same block is pasted into a second surface. It now ships as one element with
 * one correct home: the top of the list COLUMN, inside a `flex flex-col
 * min-h-0` wrapper that keeps the table's scroll containment.
 *
 * ## The notice outlives the button
 *
 * The whole block used to be gated on `allRows.length > 0`. Reasonable for the
 * button — there is nothing to empty — but it also hid the retention notice at
 * the one moment a user is most likely to want it: standing in an empty Trash
 * wondering where their mail went. The notice always renders; only the button
 * is conditional.
 */
export default function TrashNoticeBar({ retentionDays = 30, onEmpty, isEmptying = false, canEmpty = true }) {
    return (
        <div
            data-testid="trash-notice"
            className="flex items-center justify-between gap-3 px-5 py-2
                       border-b border-[#EDEBE6] bg-[#FBFAF9]"
        >
            <span className="text-[12.5px] text-[#57504B]">
                Messages in Trash are deleted permanently after {retentionDays} days.
            </span>
            {canEmpty && onEmpty && (
                <button
                    type="button"
                    data-testid="empty-trash"
                    disabled={isEmptying}
                    onClick={onEmpty}
                    className="h-8 px-3 rounded-[8px] border border-[#E4E1DB] bg-white
                               text-[13px] text-[#57504B] hover:bg-[#F4F2EE]
                               disabled:opacity-60"
                >
                    Empty Trash
                </button>
            )}
        </div>
    );
}
